import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
import { Calendar as CalendarIcon, Heart, X, CheckCircle2, Info, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSeo, SITE_NAME } from '@/lib/seo';
import { Section } from '@/components/layout/Section';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { api } from '@/lib/api';
import { resolveImageUrl } from '@/context/FeaturedImagesContext';
import { REQUEST_TYPES, SLOT_TIMES } from '@/lib/site';
import { useInspirations } from '@/context/InspirationContext';
import { TEST_IDS } from '@/constants/testIds/floreal';
import { cn } from '@/lib/utils';
import { errorMessage } from '@/lib/errorMessage';

function toISODate(d) {
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export default function Prenota() {
    useSeo({
        title: `${SITE_NAME} — Prenota un appuntamento`,
        description: "Prenota una consulenza in atelier per matrimoni, eventi, allestimenti e workshop privati. Scegli data e orario dal calendario.",
    });

    const location = useLocation();
    const navigate = useNavigate();
    const { items: inspirations, remove } = useInspirations();

    const [form, setForm] = useState({
        full_name: '', email: '', phone: '',
        request_type: location.state?.requestType || '',
        event_date: '', location: '', num_guests: '', budget: '', style: '', notes: '',
        slot_date: '', slot_time: '',
        privacy_consent: false,
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [bookedSlots, setBookedSlots] = useState([]); // [{date, time}]
    const [loadingAvailability, setLoadingAvailability] = useState(true);
    const [selectedDate, setSelectedDate] = useState(null);
    const [eventDateObj, setEventDateObj] = useState(null);
    const [calOpen, setCalOpen] = useState(false);
    const [eventCalOpen, setEventCalOpen] = useState(false);

    const reloadAvailability = useCallback(() => {
        setLoadingAvailability(true);
        return api
            .get('/appointments/availability')
            .then((res) => setBookedSlots(res.data || []))
            .catch(() => { })
            .finally(() => setLoadingAvailability(false));
    }, []);

    useEffect(() => {
        reloadAvailability();
    }, [reloadAvailability]);

    const slotsBookedForDay = useMemo(() => {
        if (!selectedDate) return [];
        const iso = toISODate(selectedDate);
        return bookedSlots.filter((s) => s.date === iso).map((s) => s.time);
    }, [selectedDate, bookedSlots]);

    const allSlotsBookedForDay = useMemo(() => {
        if (!selectedDate) return false;
        return SLOT_TIMES.every((t) => slotsBookedForDay.includes(t));
    }, [selectedDate, slotsBookedForDay]);

    const isPastOrSundayOrMonday = (d) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (d < today) return true;
        const dow = d.getDay(); // 0=Sun, 1=Mon
        return dow === 0 || dow === 1;
    };

    const validate = () => {
        const e = {};
        if (!form.full_name.trim()) e.full_name = 'Necessario';
        if (!form.email.trim()) e.email = 'Necessario';
        else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = 'Email non valida';
        if (!form.phone.trim()) e.phone = 'Necessario';
        if (!form.request_type) e.request_type = 'Scegli un tipo';
        if (!selectedDate) e.slot_date = 'Seleziona una data';
        if (!form.slot_time) e.slot_time = 'Scegli un orario';
        if (!form.privacy_consent) e.privacy_consent = 'Devi accettare la privacy';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev?.preventDefault?.();
        if (!validate()) return;
        setSubmitting(true);
        try {
            const payload = {
                full_name: form.full_name,
                email: form.email,
                phone: form.phone,
                request_type: form.request_type,
                event_date: eventDateObj ? toISODate(eventDateObj) : null,
                location: form.location || null,
                num_guests: form.num_guests ? parseInt(form.num_guests, 10) : null,
                budget: form.budget || null,
                style: form.style || null,
                inspiration_image_ids: inspirations.map((i) => i.id),
                notes: form.notes || null,
                slot_date: toISODate(selectedDate),
                slot_time: form.slot_time,
                privacy_consent: form.privacy_consent,
            };
            await api.post('/appointments', payload);
            toast.success('Richiesta inviata.', { description: 'Ti rispondiamo a breve per confermare l’orario.' });
            setSuccess(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            const status = err?.response?.status;
            const msg = errorMessage(err, 'Qualcosa non ha funzionato. Riprova tra poco.');
            if (status === 409) {
                toast.error('Questo orario è stato appena prenotato.', {
                    description: 'Scegline un altro: la disponibilità è stata aggiornata.',
                });
                // Reset the conflicting slot, refresh availability so the chip becomes disabled
                setForm((f) => ({ ...f, slot_time: '' }));
            } else {
                toast.error(typeof msg === 'string' ? msg : 'Errore durante l’invio');
            }
            // Always refresh availability after a failed attempt
            reloadAvailability();
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <section className="pt-40 sm:pt-48 pb-24">
                <div className="container-atelier max-w-xl text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="rounded-xl bg-atelier-sage/20 border border-atelier-sage/30 p-10"
                    >
                        <CheckCircle2 className="h-8 w-8 mx-auto text-atelier-sage-ink" />
                        <h1 className="mt-4 font-serif text-3xl sm:text-4xl">Grazie, ci sentiamo presto.</h1>
                        <p className="mt-3 text-muted-foreground">
                            Abbiamo ricevuto la tua richiesta per il giorno <strong>{format(selectedDate, 'd MMMM yyyy', { locale: itLocale })}</strong> alle <strong>{form.slot_time}</strong>.
                            Ti ricontatteremo a breve per confermare l&apos;orario.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3 justify-center">
                            <Button onClick={() => navigate('/')}>Torna alla home</Button>
                            <Button variant="outline" onClick={() => navigate('/galleria')}>Vai alla galleria</Button>
                        </div>
                    </motion.div>
                </div>
            </section>
        );
    }

    return (
        <>
            <section className="pt-32 sm:pt-40 pb-6">
                <div className="container-atelier max-w-3xl">
                    <p className="eyebrow mb-4">Prenota un appuntamento</p>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                        Raccontaci il tuo progetto.
                    </h1>
                    <p className="mt-5 text-muted-foreground leading-relaxed text-base sm:text-lg">
                        Scegli una data e un orario per la consulenza in atelier. Aggiungi i dettagli del tuo evento: penseremo insieme allo stile e ai fiori.
                    </p>
                </div>
            </section>

            <Section className="pt-4 sm:pt-6">
                <form data-testid={TEST_IDS.bookingForm} onSubmit={onSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
                    {/* Main form */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Contatti */}
                        <div className="rounded-xl border border-border bg-card p-6 sm:p-8">
                            <p className="eyebrow mb-4">I tuoi contatti</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="sm:col-span-2">
                                    <Label htmlFor="full_name" className="eyebrow">Nome e cognome *</Label>
                                    <Input id="full_name" data-testid={TEST_IDS.bookingFullName} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={cn('mt-1.5', errors.full_name && 'border-destructive')} />
                                    {errors.full_name && <p className="text-xs text-destructive mt-1">{errors.full_name}</p>}
                                </div>
                                <div>
                                    <Label htmlFor="email" className="eyebrow">Email *</Label>
                                    <Input id="email" data-testid={TEST_IDS.bookingEmail} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={cn('mt-1.5', errors.email && 'border-destructive')} />
                                    {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
                                </div>
                                <div>
                                    <Label htmlFor="phone" className="eyebrow">Telefono *</Label>
                                    <Input id="phone" data-testid={TEST_IDS.bookingPhone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={cn('mt-1.5', errors.phone && 'border-destructive')} />
                                    {errors.phone && <p className="text-xs text-destructive mt-1">{errors.phone}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Progetto */}
                        <div className="rounded-xl border border-border bg-card p-6 sm:p-8">
                            <p className="eyebrow mb-4">Il tuo progetto</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="sm:col-span-2">
                                    <Label className="eyebrow">Tipo di richiesta *</Label>
                                    <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
                                        <SelectTrigger data-testid={TEST_IDS.bookingServiceSelect} className={cn('mt-1.5', errors.request_type && 'border-destructive')}>
                                            <SelectValue placeholder="Scegli un servizio" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {REQUEST_TYPES.map((t) => (
                                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {errors.request_type && <p className="text-xs text-destructive mt-1">{errors.request_type}</p>}
                                </div>
                                <div>
                                    <Label className="eyebrow">Data dell&apos;evento (opzionale)</Label>
                                    <Popover open={eventCalOpen} onOpenChange={setEventCalOpen}>
                                        <PopoverTrigger asChild>
                                            <Button type="button" variant="outline" className="mt-1.5 w-full justify-start font-normal">
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {eventDateObj ? format(eventDateObj, 'd MMMM yyyy', { locale: itLocale }) : <span className="text-muted-foreground">Scegli</span>}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent align="start" className="w-auto p-0">
                                            <Calendar mode="single" selected={eventDateObj} onSelect={(d) => { setEventDateObj(d); setEventCalOpen(false); }} initialFocus locale={itLocale} />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                                <div>
                                    <Label htmlFor="location" className="eyebrow">Location evento</Label>
                                    <Input id="location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5" placeholder="Es. Villa, ristorante, casa…" />
                                </div>
                                <div>
                                    <Label htmlFor="num_guests" className="eyebrow">Numero invitati</Label>
                                    <Input id="num_guests" type="number" min={0} value={form.num_guests} onChange={(e) => setForm({ ...form, num_guests: e.target.value })} className="mt-1.5" />
                                </div>
                                <div>
                                    <Label htmlFor="budget" className="eyebrow">Budget indicativo</Label>
                                    <Input id="budget" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="mt-1.5" placeholder="Es. 1500–2500 €" />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label htmlFor="style" className="eyebrow">Stile desiderato</Label>
                                    <Input id="style" value={form.style} onChange={(e) => setForm({ ...form, style: e.target.value })} className="mt-1.5" placeholder="Es. boho, romantico, minimal, di campo…" />
                                </div>
                                <div className="sm:col-span-2">
                                    <Label htmlFor="notes" className="eyebrow">Note</Label>
                                    <Textarea id="notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" placeholder="Raccontaci qualcosa in più del tuo progetto…" />
                                </div>
                            </div>
                        </div>

                        {/* Slot picker */}
                        <div className="rounded-xl border border-border bg-card p-6 sm:p-8">
                            <p className="eyebrow mb-1">Appuntamento in atelier</p>
                            <p className="text-sm text-muted-foreground mb-5">Riceviamo solo su appuntamento, martedì–sabato.</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <Label className="eyebrow">Data *</Label>
                                    <Popover open={calOpen} onOpenChange={setCalOpen}>
                                        <PopoverTrigger asChild>
                                            <Button type="button" variant="outline" data-testid={TEST_IDS.bookingDatePicker} className={cn('mt-1.5 w-full justify-start font-normal', errors.slot_date && 'border-destructive')}>
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {selectedDate ? format(selectedDate, 'd MMMM yyyy', { locale: itLocale }) : <span className="text-muted-foreground">Scegli un giorno</span>}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent align="start" className="w-auto p-0">
                                            <Calendar
                                                mode="single"
                                                selected={selectedDate}
                                                onSelect={(d) => { setSelectedDate(d); setForm((f) => ({ ...f, slot_time: '' })); setCalOpen(false); }}
                                                disabled={isPastOrSundayOrMonday}
                                                initialFocus
                                                locale={itLocale}
                                            />
                                        </PopoverContent>
                                    </Popover>
                                    {errors.slot_date && <p className="text-xs text-destructive mt-1">{errors.slot_date}</p>}
                                </div>
                                <div>
                                    <Label className="eyebrow">Orario *</Label>
                                    {!selectedDate ? (
                                        <p className="text-sm text-muted-foreground mt-2 flex items-center gap-2">
                                            <Info className="h-4 w-4 shrink-0" /> Scegli prima una data per vedere gli orari.
                                        </p>
                                    ) : loadingAvailability ? (
                                        <p className="text-sm text-muted-foreground mt-2 flex items-center gap-2">
                                            <Loader2 className="h-4 w-4 animate-spin" /> Carico la disponibilità…
                                        </p>
                                    ) : allSlotsBookedForDay ? (
                                        <p
                                            data-testid={TEST_IDS.bookingNoSlotsWarning}
                                            className="text-sm text-destructive mt-2 flex items-start gap-2 rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2"
                                        >
                                            <Info className="h-4 w-4 shrink-0 mt-0.5" />
                                            <span>Tutti gli orari di questa giornata sono già prenotati. Scegli un altro giorno.</span>
                                        </p>
                                    ) : null}
                                    <div className="mt-1.5 flex flex-wrap gap-2">
                                        {SLOT_TIMES.map((t) => {
                                            const isBooked = slotsBookedForDay.includes(t);
                                            const isSelected = form.slot_time === t;
                                            const disabled = !selectedDate || isBooked || loadingAvailability;
                                            return (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    data-testid={TEST_IDS.bookingSlotChip}
                                                    data-slot-time={t}
                                                    data-slot-state={isBooked ? 'booked' : isSelected ? 'selected' : 'available'}
                                                    disabled={disabled}
                                                    onClick={() => setForm({ ...form, slot_time: t })}
                                                    aria-pressed={isSelected}
                                                    aria-label={`${t}${isBooked ? ' — non disponibile' : ''}`}
                                                    className={cn(
                                                        'h-10 px-4 rounded-full border text-sm transition-colors',
                                                        isSelected
                                                            ? 'bg-primary border-primary text-primary-foreground'
                                                            : 'bg-card border-border hover:bg-secondary text-foreground',
                                                        disabled && 'opacity-40 cursor-not-allowed',
                                                        isBooked && 'line-through',
                                                    )}
                                                >
                                                    {t}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {errors.slot_time && <p className="text-xs text-destructive mt-2">{errors.slot_time}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Privacy + submit */}
                        <div className="rounded-xl border border-border bg-card p-6 sm:p-8">
                            <div className="flex items-start gap-3">
                                <Checkbox id="privacy" data-testid={TEST_IDS.bookingPrivacy} checked={form.privacy_consent} onCheckedChange={(c) => setForm({ ...form, privacy_consent: !!c })} />
                                <label htmlFor="privacy" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                                    Acconsento al trattamento dei miei dati per essere ricontattato/a sulla mia richiesta, come descritto nell'<Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">informativa privacy</Link>. Usiamo i tuoi dati solo per rispondere alla richiesta.
                                </label>
                            </div>
                            {errors.privacy_consent && <p className="text-xs text-destructive mt-2">{errors.privacy_consent}</p>}
                            <Button type="submit" data-testid={TEST_IDS.bookingSubmit} disabled={submitting} className="mt-6 h-12 px-6 w-full sm:w-auto">
                                {submitting ? 'Un momento…' : 'Invia richiesta'}
                            </Button>
                        </div>
                    </div>

                    {/* Inspirations sidebar */}
                    <aside className="lg:col-span-1">
                        <div data-testid={TEST_IDS.bookingInspirations} className="rounded-xl border border-border bg-atelier-ivory p-6 sticky top-24">
                            <p className="eyebrow mb-2">Le tue ispirazioni</p>
                            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                                {inspirations.length === 0 ? 'Sfoglia la galleria e salva le immagini che ami: appariranno qui.' : 'Le immagini che hai salvato dalla galleria sono allegate alla richiesta.'}
                            </p>
                            {inspirations.length > 0 && (
                                <div className="grid grid-cols-3 gap-2">
                                    {inspirations.slice(0, 12).map((it) => (
                                        <div key={it.id} className="relative group rounded-md overflow-hidden border border-border">
                                            <img src={resolveImageUrl(it.image_url)} alt={it.title} loading="lazy" className="w-full aspect-square object-cover" />
                                            <button type="button" onClick={() => remove(it.id)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/85 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Rimuovi">
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {inspirations.length === 0 && (
                                <Button variant="outline" type="button" onClick={() => navigate('/galleria')} className="mt-2 w-full">
                                    <Heart className="h-4 w-4 mr-2" /> Apri la galleria
                                </Button>
                            )}
                        </div>
                    </aside>
                </form>
            </Section>
        </>
    );
}
